/**
 * @fileoverview Component for generating and downloading packages and groups reports.
 * This page allows users to filter reports by campus, modality, and program
 * before downloading them.
 */

import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { Text, Box, Button, toast } from "@foris/avocado-suite";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import { Header } from "@common/components";
import Layout from "@common/layout/Layout";
import CampusSelect from "@modules/shared/selectors/Selectors/Campus";
import ModalitySelect from "@modules/shared/selectors/Selectors/Modality";
import ProgramSelect from "@modules/shared/selectors/Selectors/Program";
import { PathParams } from "@edhtypes/general";
import scss from "./packagesAndGroupsReport.module.scss";

/**
 * PackagesAndGroupsReport component - Provides an interface for users to filter and download
 * reports about packages and groups.
 *
 * Features:
 * - Filter selection by campus, modality, and program
 * - Hierarchical filtering (campus/modality selection affects program options)
 * - Report download with applied filters
 * - Navigation back to previous page
 */
const PackagesAndGroupsReport: FC = () => {
  const { origin, scenario, workspace }: PathParams = useParams();
  const history = useHistory();
  const { getGroupsAndPackagesReportUrl, homeUrl, adaptedHomeUrl } = useNavigationUrl();

  const { t } = useTranslation();

  const [selectedCampus, setSelectedCampus] = useState(null);
  const [selectedModality, setSelectedModality] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);

  const hasSomeFilterSelected = !!selectedCampus || !!selectedModality || !!selectedProgram;

  /**
   * Construct the report URL with selected filters
   * Memoized to prevent unnecessary recalculations when component rerenders
   */
  const groupsAndPackageReportUrl = useMemo(() => {
    return getGroupsAndPackagesReportUrl({
      campus: selectedCampus,
      modality: selectedModality,
      program: selectedProgram,
    });
  }, [selectedCampus, selectedModality, selectedProgram, getGroupsAndPackagesReportUrl]);

  /**
   * Handle campus selection change
   * When campus changes, reset program selection as it depends on campus
   */
  const handleSelectCampus = (value: string | null) => {
    setSelectedCampus(value);
    setSelectedProgram(null);
  };

  /**
   * Handle modality selection change
   * When modality changes, reset program selection as it depends on modality
   */
  const handleSelectModality = (value: string | null) => {
    setSelectedModality(value);
    setSelectedProgram(null);
  };

  /**
   * Handle navigation back to previous page
   * Uses browser history when available, otherwise redirects to home page
   */
  const handleBack = () => {
    if (history.length > 2) {
      history.goBack();
    } else {
      history.push(homeUrl(workspace, scenario, origin));
    }
  };

  /**
   * Handle report download action
   * Opens the report URL in a new tab if filters are selected
   */
  const handleDownloadReport = () => {
    if (groupsAndPackageReportUrl && hasSomeFilterSelected) {
      toast.success(t("package-and-group-report.snackbar.title"), {
        autoClose: 5000,
      });

      window.open(groupsAndPackageReportUrl, "_blank");
    }
  };

  return (
    <Layout contextSearch>
      <Header
        breadcrumbItems={[
          {
            title: t("package-and-group-report.breadcrumb.prev"),
            path: adaptedHomeUrl(),
          },
          {
            title: t("package-and-group-report.breadcrumb.current"),
          },
        ]}
        title={t("package-and-group-report.title")}
      />
      <main className={scss.packagesAndGroupsReport}>
        <Text weight="medium" type="lg">
          {t("package-and-group-report.information-box.title")}
        </Text>
        <Box className={scss.packagesAndGroupsReport_informationBox}>
          <div className={scss.content}>
            <CampusSelect
              label={t("package-and-group-report.information-box.select-campus.title")}
              placeholder={t("package-and-group-report.information-box.select-campus.placeholder")}
              onCallback={opt => handleSelectCampus(opt ? opt.value : null)}
            />

            <ModalitySelect
              label={t("package-and-group-report.information-box.select-modality.title")}
              placeholder={t(
                "package-and-group-report.information-box.select-modality.placeholder",
              )}
              shouldHasDepartment={false}
              onCallback={opt => handleSelectModality(opt ? opt.value : null)}
            />

            <ProgramSelect
              key={`program-${selectedCampus}-${selectedModality}`}
              label={t("package-and-group-report.information-box.select-program.title")}
              placeholder={t("package-and-group-report.information-box.select-program.placeholder")}
              filterBy="modality"
              campus={selectedCampus}
              modality={selectedModality}
              shouldHaveFilters={false}
              onCallback={opt => setSelectedProgram(opt ? opt.value : null)}
            />
          </div>
        </Box>
        <div className={scss.packagesAndGroupsReport_actions}>
          <Button variant="ghost" onClick={handleBack}>
            {t("package-and-group-report.btn-cancel")}
          </Button>

          <Button
            leftIcon="download"
            disabled={!hasSomeFilterSelected}
            onClick={handleDownloadReport}
          >
            {t("package-and-group-report.btn-download")}
          </Button>
        </div>
      </main>
    </Layout>
  );
};

export default PackagesAndGroupsReport;
